import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import RevenueHero from '@/pages/demo/revenue/components/RevenueHero';
import SponsorWorkflow from '@/pages/demo/revenue/components/SponsorWorkflow';
import TournamentWorkflow from '@/pages/demo/revenue/components/TournamentWorkflow';
import RegionExplorer from '@/pages/demo/revenue/components/RegionExplorer';
import RevenueDashboard from '@/pages/demo/revenue/components/RevenueDashboard';

type Tab = 'sponsor' | 'tournament' | 'regions' | 'dashboard';

const TABS: { id: Tab; label: string; short: string; icon: string }[] = [
  { id: 'sponsor', label: 'Sponsorship sales', short: 'Sponsorship', icon: 'ri-megaphone-line' },
  { id: 'tournament', label: 'Tournament monetisation', short: 'Tournaments', icon: 'ri-trophy-line' },
  { id: 'regions', label: 'Multi-region publishing', short: 'Multi-region', icon: 'ri-map-2-line' },
  { id: 'dashboard', label: 'Revenue dashboard', short: 'Dashboard', icon: 'ri-bar-chart-2-line' },
];

export default function RevenueDemoPage() {
  const [tab, setTab] = useState<Tab>('sponsor');

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Header />
      <main>
        <RevenueHero />

        <div className="bg-accent-500 px-4 md:px-8 py-3 flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center text-foreground-950 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
          <p className="text-sm text-foreground-950 font-medium">
            Conceptual Revenue Demo &mdash; No Real Payments, Registrations, or Automated Campaigns. All workflows use sample data and local state only.
          </p>
        </div>

        {/* How to present + back to guide */}
        <div className="bg-background-100 border-b border-background-200 px-4 md:px-8 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Five-minute walkthrough</p>
              <p className="mt-1 text-sm text-foreground-600 max-w-2xl">
                Move through the four tabs in order: sell a sponsorship, monetise a tournament, switch regions, then land the money on the revenue dashboard.
              </p>
            </div>
            <Link to="/demo" className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors shrink-0">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-route-line"></i></span>
              Back to demo guide
            </Link>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="bg-background-50 border-b border-background-200 px-4 md:px-8 py-4 sticky top-0 z-20">
          <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-label font-bold uppercase tracking-wider text-[11px] whitespace-nowrap cursor-pointer transition-colors ${tab === t.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className={t.icon}></i></span>
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.short}</span>
              </button>
            ))}
          </div>
        </div>

        <section className="px-4 md:px-8 py-10 md:py-14">
          {tab === 'sponsor' && <SponsorWorkflow />}
          {tab === 'tournament' && <TournamentWorkflow />}
          {tab === 'regions' && <RegionExplorer />}
          {tab === 'dashboard' && <RevenueDashboard />}
        </section>

        <section className="bg-background-100 border-t border-background-200 px-4 md:px-8 py-12 md:py-16">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">Keep exploring the concept</h2>
              <p className="mt-2 text-foreground-600 text-sm max-w-xl">See how the AI-assisted publishing workflow and the management preview fit alongside this revenue story.</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link to="/how-it-works" className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-800 font-label font-bold uppercase tracking-wider text-xs px-4 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>
                AI workflow concept
              </Link>
              <Link to="/demo" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-xs px-4 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-route-line"></i></span>
                Demo guide
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}